import get from "lodash.get";
import { FC } from "react";
import { Controller, RegisterOptions, useFormContext, useFormState } from "react-hook-form";

import { AsyncFilterableSinglePicker } from "src/components/Inputs/AsyncFilterableSinglePicker";
import { useLazySearchCasesQuery } from "src/graphql/anti-fraud/queries/cases/SearchCasesQuery";
import { mapToCaseSummary } from "src/mappers/case.mappers";
import { CaseSummary } from "src/types/case";

export type CaseFilterableSinglePickerProps = {
  name: string;
  id?: string;
  helperText?: string;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  caseTypeCodes?: string[];
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">;
};

export const CaseFilterableSinglePicker: FC<CaseFilterableSinglePickerProps> = ({
  id,
  helperText,
  label,
  placeholder,
  readOnly = false,
  caseTypeCodes = [],
  name,
  rules,
}) => {
  const [searchCases, { error: queryError }] = useLazySearchCasesQuery({});

  const loadOptions = async (search: string) => {
    const { data } = await searchCases({
      variables: {
        searchString: search && search.length > 0 ? search : null,
        offset: 0,
        limit: 20,
        onlyScored: null,
        caseTypeCodes,
        sorting: {
          path: "date",
          source: "CASE_PROPERTY",
          direction: "DESC",
        },
      },
      fetchPolicy: "network-only",
    });
    const cases: CaseSummary[] = (data?.caseSearch?.cases ?? data?.caseSearch?.cases ?? []).map(
      mapToCaseSummary
    );
    return cases;
  };

  const { control } = useFormContext();
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { value, onChange } }) => (
        <AsyncFilterableSinglePicker<CaseSummary>
          id={id}
          readOnly={readOnly}
          invalid={!!error}
          invalidText={error?.message as string}
          helperText={helperText}
          placeholder={placeholder}
          label={label}
          getOptionLabel={(c) => c.request ?? ""}
          getOptionValue={(c) => c.id}
          value={(value as CaseSummary | undefined) ?? null}
          loadOptions={loadOptions}
          loadingOptionsError={!!queryError}
          onChange={(c) => onChange(c ?? null)}
        />
      )}
    />
  );
};

export default CaseFilterableSinglePicker;
